import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoCreateOutline, IoAddOutline } from 'react-icons/io5';
import { MdDragIndicator } from 'react-icons/md';

const HEADER_MENU_SLUG = 'header';
const HEADER_MENU_ID = 1;

function buildDisplayOrder(items) {
  const byParent = {};
  items.forEach((i) => {
    const pid = i.parent_id != null ? i.parent_id : 0;
    if (!byParent[pid]) byParent[pid] = [];
    byParent[pid].push(i);
  });
  [0, ...(byParent[0] || []).map((i) => i.id)].forEach((pid) => {
    if (byParent[pid]) byParent[pid].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  });
  const out = [];
  const walk = (pid) => {
    (byParent[pid] || []).forEach((i) => {
      out.push(i);
      walk(i.id);
    });
  };
  walk(0);
  return out;
}

function SortableRow({ item, isTopLevel, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !isTopLevel,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell sx={{ pl: isTopLevel ? 2 : 4, width: 40 }}>
        {isTopLevel ? (
          <span {...attributes} {...listeners} style={{ cursor: 'grab', display: 'flex' }} aria-label="Drag to reorder">
            <MdDragIndicator size={20} />
          </span>
        ) : null}
      </TableCell>
      <TableCell>{item.label}</TableCell>
      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.url}</TableCell>
      <TableCell>
        {item.is_mega ? 'Mega' : item.parent_id != null ? 'Child' : 'Link'}
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" onClick={() => onEdit(item)} aria-label="Edit">
          <IoCreateOutline size={18} />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(item)} aria-label="Delete" color="error">
          ×
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

export default function AdminMenu() {
  const [menus, setMenus] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    label: '',
    url: '#',
    parent_id: '',
    sort_order: 0,
    is_mega: false,
    mega_config: null,
  });
  const [activeId, setActiveId] = useState(null);

  const loadMenus = () => api('/api/admin-menu/menus').then(setMenus).catch((e) => setError(e.message));
  const loadItems = () =>
    api(`/api/admin-menu/menus/${HEADER_MENU_SLUG}/items`)
      .then(setItems)
      .catch((e) => setError(e.message));

  useEffect(() => {
    setLoading(true);
    loadMenus()
      .then(() => loadItems())
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const displayOrder = useMemo(() => buildDisplayOrder(items), [items]);
  const topLevelIds = useMemo(() => items.filter((i) => i.parent_id == null).sort((a, b) => a.sort_order - b.sort_order || a.id - b.id).map((i) => i.id), [items]);

  const parentOptions = useMemo(() => {
    const tops = items.filter((i) => i.parent_id == null).sort((a, b) => a.sort_order - b.sort_order);
    return [{ id: '', label: '— Top level' }, ...tops.map((t) => ({ id: t.id, label: t.label }))];
  }, [items]);

  const handleOpenDialog = (item = null) => {
    setEditing(item);
    setForm({
      label: item ? item.label : '',
      url: item ? item.url : '#',
      parent_id: item?.parent_id != null ? String(item.parent_id) : '',
      sort_order: item != null ? item.sort_order : displayOrder.length,
      is_mega: item?.is_mega || false,
      mega_config: item?.mega_config || { columns: [] },
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        menu_id: HEADER_MENU_ID,
        parent_id: form.parent_id ? Number(form.parent_id) : null,
        label: form.label.trim(),
        url: (form.url || '#').trim(),
        sort_order: Number(form.sort_order) || 0,
        is_mega: form.is_mega,
        mega_config: form.is_mega && form.mega_config?.columns?.length ? form.mega_config : null,
      };
      if (editing) {
        await api(`/api/admin-menu/menu-items/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await api('/api/admin-menu/menu-items', { method: 'POST', body: JSON.stringify(payload) });
      }
      handleCloseDialog();
      loadItems();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.label}"?${item.parent_id == null ? ' Child items will be deleted too.' : ''}`)) return;
    setError('');
    try {
      await api(`/api/admin-menu/menu-items/${item.id}`, { method: 'DELETE' });
      loadItems();
    } catch (err) {
      setError(err.message);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: () => null })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = topLevelIds.indexOf(active.id);
    const newIndex = topLevelIds.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(topLevelIds, oldIndex, newIndex);
    try {
      await api(`/api/admin-menu/menus/${HEADER_MENU_ID}/order`, { method: 'PUT', body: JSON.stringify({ order: reordered }) });
      loadItems();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
      <CardHeader
        title="Menu"
        subheader="Header menu – drag top-level rows to reorder"
        action={
          <Button startIcon={<IoAddOutline size={18} />} variant="contained" onClick={() => handleOpenDialog()} sx={{ textTransform: 'none' }}>
            Add item
          </Button>
        }
      />
      <CardContent>
        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
        {items.length === 0 ? (
          <AdminEmptyState message="No menu items. Run schema-menu.sql and seed-menu.js, or add items above." icon={MdDragIndicator} />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={handleDragEnd}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>Label</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <SortableContext items={topLevelIds} strategy={verticalListSortingStrategy}>
                    {displayOrder.map((item) => (
                      <SortableRow
                        key={item.id}
                        item={item}
                        isTopLevel={item.parent_id == null}
                        onEdit={handleOpenDialog}
                        onDelete={handleDelete}
                      />
                    ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </TableContainer>
            <DragOverlay>
              {activeId ? (
                <TableRow sx={{ bgcolor: 'background.paper', boxShadow: 2 }}>
                  <TableCell sx={{ width: 40 }}><MdDragIndicator size={20} /></TableCell>
                  <TableCell>{items.find((i) => i.id === activeId)?.label}</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>—</TableCell>
                </TableRow>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit menu item' : 'Add menu item'}</DialogTitle>
        <form onSubmit={handleSave}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0 }}>
              <TextField size="small" label="Label" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} required fullWidth />
              <TextField size="small" label="URL" value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="/shop" fullWidth />
              <FormControl size="small" fullWidth>
                <InputLabel>Parent</InputLabel>
                <Select value={form.parent_id} label="Parent" onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}>
                  {parentOptions.map((opt) => (
                    <MenuItem key={opt.id || 'none'} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" type="number" label="Sort order" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))} inputProps={{ min: 0 }} sx={{ width: 120 }} />
              <FormControlLabel control={<Checkbox checked={form.is_mega} onChange={(e) => setForm((f) => ({ ...f, is_mega: e.target.checked }))} />} label="Mega menu (multi-column dropdown)" />
              {form.is_mega && (
                <MegaConfigEditor value={form.mega_config} onChange={(mega_config) => setForm((f) => ({ ...f, mega_config }))} />
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">{editing ? 'Update' : 'Create'}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Card>
  );
}

function MegaConfigEditor({ value, onChange }) {
  const config = value || { columns: [] };
  const columns = config.columns || [];

  const setColumns = (next) => onChange({ ...config, columns: next });

  const addColumn = () => setColumns([...columns, { title: '', type: 'links', links: [], imageUrl: '', link: '' }]);
  const removeColumn = (index) => setColumns(columns.filter((_, i) => i !== index));
  const updateColumn = (index, field, val) => {
    const next = [...columns];
    next[index] = { ...next[index], [field]: val };
    setColumns(next);
  };
  const addLink = (colIndex) => {
    const next = [...columns];
    next[colIndex].links = [...(next[colIndex].links || []), { label: '', url: '' }];
    setColumns(next);
  };
  const removeLink = (colIndex, linkIndex) => {
    const next = [...columns];
    next[colIndex].links = (next[colIndex].links || []).filter((_, i) => i !== linkIndex);
    setColumns(next);
  };
  const updateLink = (colIndex, linkIndex, field, val) => {
    const next = [...columns];
    const links = [...(next[colIndex].links || [])];
    links[linkIndex] = { ...links[linkIndex], [field]: val };
    next[colIndex].links = links;
    setColumns(next);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Mega menu columns</Typography>
      {columns.map((col, colIndex) => (
        <Paper key={colIndex} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <TextField size="small" label="Column title" value={col.title || ''} onChange={(e) => updateColumn(colIndex, 'title', e.target.value)} sx={{ width: 200 }} />
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select value={col.type || 'links'} onChange={(e) => updateColumn(colIndex, 'type', e.target.value)}>
                <MenuItem value="links">Links</MenuItem>
                <MenuItem value="image">Image</MenuItem>
              </Select>
            </FormControl>
            <Button size="small" color="error" onClick={() => removeColumn(colIndex)}>Remove</Button>
          </Box>
          {col.type === 'image' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField size="small" fullWidth label="Image URL" value={col.imageUrl || ''} onChange={(e) => updateColumn(colIndex, 'imageUrl', e.target.value)} />
              <TextField size="small" fullWidth label="Link" value={col.link || ''} onChange={(e) => updateColumn(colIndex, 'link', e.target.value)} />
            </Box>
          ) : (
            <Box>
              {(col.links || []).map((link, linkIndex) => (
                <Box key={linkIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField size="small" label="Label" value={link.label || ''} onChange={(e) => updateLink(colIndex, linkIndex, 'label', e.target.value)} sx={{ flex: 1 }} />
                  <TextField size="small" label="URL" value={link.url || ''} onChange={(e) => updateLink(colIndex, linkIndex, 'url', e.target.value)} sx={{ flex: 1 }} />
                  <IconButton size="small" onClick={() => removeLink(colIndex, linkIndex)}>×</IconButton>
                </Box>
              ))}
              <Button size="small" onClick={() => addLink(colIndex)}>+ Add link</Button>
            </Box>
          )}
        </Paper>
      ))}
      <Button size="small" variant="outlined" onClick={addColumn}>+ Add column</Button>
    </Box>
  );
}
